import React, { useState, useEffect } from 'react'
import { supabase, TABLES } from '../../config/supabase'
import { useAuth } from '../../contexts/AuthContext'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Card from '../ui/Card'
import Button from '../ui/Button'

const { FiDatabase, FiRefreshCw, FiCheck, FiX, FiAlertCircle } = FiIcons

const SupabaseDebugger = () => {
  const { user, family } = useAuth()
  const [debugData, setDebugData] = useState(null)
  const [loading, setLoading] = useState(false)

  const runDiagnostics = async () => {
    setLoading(true)
    const diagnostics = {
      timestamp: new Date().toISOString(),
      user: {
        authenticated: !!user,
        id: user?.id,
        email: user?.email
      },
      family: {
        loaded: !!family,
        id: family?.id,
        name: family?.name,
        children_count: family?.children?.length || 0
      },
      tables: {}
    }

    try {
      // Test each table
      for (const [tableName, tableKey] of Object.entries(TABLES)) {
        try {
          const { data, error, count } = await supabase
            .from(tableKey)
            .select('*', { count: 'exact' })
            .limit(1)

          diagnostics.tables[tableName] = {
            accessible: !error,
            error: error?.message,
            count: count || 0,
            sample_data: data?.[0] || null
          }
        } catch (err) {
          diagnostics.tables[tableName] = {
            accessible: false,
            error: err.message,
            count: 0,
            sample_data: null
          }
        }
      }

      // Test family-specific data if family exists
      if (family?.id) {
        try {
          const { data: tasks, error: tasksError } = await supabase
            .from(TABLES.TASKS)
            .select('*')
            .eq('family_id', family.id)

          const { data: completions, error: completionsError } = await supabase
            .from(TABLES.TASK_COMPLETIONS)
            .select('*')
            .eq('family_id', family.id)

          diagnostics.family_data = {
            tasks: {
              count: tasks?.length || 0,
              error: tasksError?.message,
              sample: tasks?.[0]
            },
            completions: {
              count: completions?.length || 0,
              error: completionsError?.message,
              sample: completions?.[0]
            }
          }
        } catch (err) {
          diagnostics.family_data = {
            error: err.message
          }
        }
      }

      setDebugData(diagnostics)
    } catch (error) {
      setDebugData({
        error: error.message,
        timestamp: new Date().toISOString()
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (user) {
      runDiagnostics()
    }
  }, [user, family])

  if (!user) {
    return (
      <Card>
        <div className="text-center py-8">
          <SafeIcon icon={FiAlertCircle} className="text-red-500 text-2xl mb-2" />
          <p className="text-gray-600">Please sign in to run diagnostics</p>
        </div>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <SafeIcon icon={FiDatabase} className="text-primary-500" />
            Supabase Connection Diagnostics
          </h3>
          <Button variant="outline" size="sm" onClick={runDiagnostics} loading={loading}>
            <SafeIcon icon={FiRefreshCw} className="text-sm" />
            Run Diagnostics
          </Button>
        </div>

        {debugData && (
          <div className="space-y-4">
            {/* User Status */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">User Authentication</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <SafeIcon icon={debugData.user.authenticated ? FiCheck : FiX} 
                    className={debugData.user.authenticated ? 'text-green-500' : 'text-red-500'} />
                  <span>Authenticated: {debugData.user.authenticated ? 'Yes' : 'No'}</span>
                </div>
                <div>User ID: {debugData.user.id || 'N/A'}</div>
                <div>Email: {debugData.user.email || 'N/A'}</div>
              </div>
            </div>

            {/* Family Status */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Family Data</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <SafeIcon icon={debugData.family.loaded ? FiCheck : FiX} 
                    className={debugData.family.loaded ? 'text-green-500' : 'text-red-500'} />
                  <span>Family Loaded: {debugData.family.loaded ? 'Yes' : 'No'}</span>
                </div>
                <div>Family ID: {debugData.family.id || 'N/A'}</div>
                <div>Family Name: {debugData.family.name || 'N/A'}</div>
                <div>Children Count: {debugData.family.children_count}</div>
              </div>
            </div>

            {/* Table Access */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Database Tables</h4>
              <div className="space-y-2">
                {Object.entries(debugData.tables).map(([tableName, tableData]) => (
                  <div key={tableName} className="flex items-center justify-between p-2 bg-white rounded border">
                    <div className="flex items-center gap-2">
                      <SafeIcon icon={tableData.accessible ? FiCheck : FiX} 
                        className={tableData.accessible ? 'text-green-500' : 'text-red-500'} />
                      <span className="font-medium">{tableName}</span>
                      <span className="text-sm text-gray-500">({tableData.count} records)</span>
                    </div>
                    {tableData.error && (
                      <span className="text-xs text-red-600">{tableData.error}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Family-Specific Data */}
            {debugData.family_data && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Family-Specific Data</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-2 bg-white rounded border">
                    <span>Tasks: {debugData.family_data.tasks?.count || 0}</span>
                    {debugData.family_data.tasks?.error && (
                      <span className="text-xs text-red-600">{debugData.family_data.tasks.error}</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between p-2 bg-white rounded border">
                    <span>Completions: {debugData.family_data.completions?.count || 0}</span>
                    {debugData.family_data.completions?.error && (
                      <span className="text-xs text-red-600">{debugData.family_data.completions.error}</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Raw Debug Data */}
            <details className="p-4 bg-gray-50 rounded-lg">
              <summary className="font-medium text-gray-900 cursor-pointer">Raw Debug Data</summary>
              <pre className="mt-2 text-xs text-gray-600 overflow-auto">
                {JSON.stringify(debugData, null, 2)}
              </pre>
            </details>
          </div>
        )}
      </Card>
    </div>
  )
}

export default SupabaseDebugger